import React, { useEffect, useRef, useState } from 'react';
import { CloseIcon, ProviderConsumer } from '@fluentui/react-northstar';
import i18next from 'i18next';
import { ThemeColorScheme } from '../../common/TeamsTheme';
import { CELL_BOX, SAVE_ERROR_COLOR, SaveOutcome, markClass, savingCell, useCellTimers, useSaveMark, focusCellBack } from './inlineCell';
import { CategoryOption, listWidthRows, matchingOptions, optionName, resolveOption } from './inlineCategoryOptions';
import { TypeaheadList, revealRow, useListPlacement } from './TypeaheadList';
import { hoverTitle } from './emptyCell';
import { PENDING_LIMIT_MS, dropPending, holdPending, pendingKey, pendingValue, settlePending } from './pendingCellSaves';

/** A list under a grid cell draws in the row's own 12px, not the ticket page's 14px. */
const CELL_LIST_SIZE = 12;

interface Props {
  ticketId: number;
  /** The id the ticket holds now. */
  valueId: number | null;
  options: CategoryOption[];
  /** The column's name, for the box's accessible name. */
  label: string;
  /**
   * What the cell's save state (value being saved, green box, last-edited box) is kept under, by ticket - the column's
   * name unless given. A custom property passes its id: one named like a built-in column shared that column's state.
   */
  stateKey?: string;
  /** The cell at rest, given the id to show - the saved one, or the one being saved. */
  renderResting: (id: number | null) => React.ReactNode;
  /** The text the cell reads as at rest (tooltip and accessible name). */
  restingTitle: (id: number | null) => string;
  /**
   * Saves the chosen id, asking whatever it needs to on the way (a resolution note, a warning), and asks the
   * list for the page again. Resolves with how that ended - see SaveOutcome.
   */
  commit: (id: number) => Promise<SaveOutcome>;
  /**
   * A dialog is asking about this cell's save (Status's resolution note or open-work warning): the cell stays
   * outlined and its row tinted under the dialog, so the agent can see which ticket it is about.
   */
  held?: boolean;
  /**
   * The value can be emptied - an optional single-pick list custom property (Shaun, 2026-10-19): an X in the box,
   * or the text deleted, saves no value (commit is given null). Type, Category, Priority and Status cannot be
   * emptied, and nor can true/false - it stays True or False, as the ticket page's checkbox.
   */
  clearable?: boolean;
}

interface PendingSave { ticketId: number; categoryId: number | null }

/**
 * The pending save if it is for this ticket - always `pending` itself or null. Callers must not test
 * `pending?.ticketId === ticketId` and then read `pending.categoryId`: a row with no Id yet (a ticket just added from
 * Add Ticket) has an undefined ticketId, which matched the undefined of no pending save, and that read blanked the
 * page. Here a match with no pending save still returns null.
 */
const pendingFor = (pending: PendingSave | null, ticketId: number): PendingSave | null => {
  if (pending?.ticketId !== ticketId) return null;
  return pending;
};

/**
 * A ticket list cell typable in place - the Outlook pane's combobox. Picking from the list saves at once; a
 * name typed in full saves when the cell is left (click away, Tab, or Enter). Escape puts the old value back. The list is drawn on the body so the cell cannot clip it,
 * and picking from it keeps focus in the box, so losing focus always means leaving.
 */
export const InlineComboCell = ({ ticketId, valueId, options, label, stateKey, renderResting, restingTitle, commit, held, clearable }: Props) => {
  const categoryId = valueId;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const [draftId, setDraftId] = useState<number | null>(categoryId);
  const [listOpen, setListOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [active, setActive] = useState(-1);
  /** Shown from a save until the refetched row arrives, for this ticket only (pendingCellSaves). */
  const key = pendingKey(stateKey ?? label, ticketId);
  const waiting = pendingValue(key);
  const pending: PendingSave | null = waiting ? { ticketId, categoryId: waiting.value } : null;
  const [, redraw] = useState(0);
  const setPending = (next: PendingSave | null) => {
    if (next) holdPending(key, next.categoryId);
    else dropPending(key);
    redraw(n => n + 1);
  };
  const [saving, setSaving] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const restingRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);
  /** Set once the box has been left, so the blur that follows a keyboard exit does not leave it twice. */
  const leftRef = useRef(true);
  /** Set when the keyboard leaves the box: focus comes back to the cell rather than moving on down the page. */
  const refocusRef = useRef(false);
  /** A highlighted option that Tab takes as it leaves - state set in the same keystroke is not yet readable. */
  const takenIdRef = useRef<number | null>(null);
  /** The box's text as typed - leave() can run in the same keystroke as the change, before state is readable. */
  const textRef = useRef('');
  const saveMark = useSaveMark(ticketId, categoryId, saving, stateKey ?? label);
  const timers = useCellTimers();

  // Rows are keyed by position, so a refetch can hand this cell a different ticket - whose pending save, if it has
  // one, comes with it (pendingSaves).
  useEffect(() => {
    setEditing(false);
    setListOpen(false);
    timers.clear();
  }, [ticketId]);

  // The reloaded row has arrived - with the saved value, or with whatever the server made of it. Only when this
  // ticket's own value moved: a cell handed another ticket sees a different value too.
  const shownRef = useRef({ ticketId, categoryId });
  useEffect(() => {
    const before = shownRef.current;
    shownRef.current = { ticketId, categoryId };
    if (before.ticketId === ticketId && before.categoryId !== categoryId && pending) setPending(null);
  }, [ticketId, categoryId]);

  const shown = matchingOptions(options, filter);

  // Sized to every option, not just the ones showing, so the list does not change width as the agent types.
  // As wide as what it shows: the tree's names while nothing is typed, the full paths once something is.
  const widthRows = listWidthRows(options, filter);
  const position = useListPlacement(inputRef, listRef, editing && listOpen, widthRows.map(row => row.text), CELL_LIST_SIZE, false, widthRows.map(row => row.indent));

  // Moving the lit row scrolls only as far as needed; where the list opens is TypeaheadList's (centred).
  useEffect(() => {
    if (active >= 0) revealRow(listRef.current, active);
  }, [active]);

  const startEditing = () => {
    saveMark.opening();
    const forThis = pendingFor(pending, ticketId);
    const current = forThis ? forThis.categoryId : categoryId;
    cancelledRef.current = false;
    leftRef.current = false;
    takenIdRef.current = null;
    setText(optionName(options, current));
    textRef.current = optionName(options, current);
    setDraftId(current);
    setFilter('');
    // The current value opens lit, as well as bold, and in view.
    setActive(options.findIndex(option => option.id === current));
    setListOpen(true);
    setEditing(true);
  };

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    } else if (refocusRef.current) {
      refocusRef.current = false;
      focusCellBack(restingRef.current);
    }
  }, [editing]);

  // A saved value is shown until the reloaded row arrives - letting go when the save returns showed the old value
  // for a moment, because the grid takes the new rows a render or two later. A failed save reopens the box with
  // the agent's choice in it, boxed red, to try again or Escape; anything else puts the old value back.
  const save = async (id: number | null) => {
    setPending({ ticketId, categoryId: id });
    setSaving(true);
    const outcome = await commit(id);
    setSaving(false);
    saveMark.show(outcome);
    if (outcome !== 'saved') {
      setPending(null);
      if (outcome === 'failed') reopenWith(id);
      return;
    }
    settlePending(key);
    // The value lapses by itself; this draws the cell again once it has, if it still shows this ticket.
    timers.later(() => redraw(n => n + 1), PENDING_LIMIT_MS);
  };

  const reopenWith = (id: number) => {
    cancelledRef.current = false;
    leftRef.current = false;
    takenIdRef.current = null;
    setText(optionName(options, id));
    textRef.current = optionName(options, id);
    setDraftId(id);
    setFilter('');
    setActive(-1);
    setListOpen(false);
    setEditing(true);
  };

  // Every way out of the box runs through here - a click away through blur, the keyboard directly.
  const leave = () => {
    if (leftRef.current) return;
    leftRef.current = true;
    setListOpen(false);
    setEditing(false);
    if (cancelledRef.current) return;
    const id = takenIdRef.current ?? draftId;
    const forThis = pendingFor(pending, ticketId);
    const current = forThis ? forThis.categoryId : categoryId;
    // Emptied: a clearable cell saves no value; any other puts the old value back.
    const emptied = clearable && id == null && !textRef.current.trim();
    if ((id != null || emptied) && id !== current) save(id);
  };

  /** The X: empties the box and leaves it, which saves no value (leave). */
  const clear = () => {
    setText('');
    setDraftId(null);
    takenIdRef.current = null;
    textRef.current = '';
    leaveByKeyboard();
  };

  // Left directly rather than through blur(): a box that never had focus would not blur, and stay open.
  const leaveByKeyboard = () => {
    refocusRef.current = true;
    leave();
  };

  // Picking from the list is the decision, so it saves at once rather than waiting for the box to be left.
  const choose = (option: CategoryOption) => {
    setText(option.name);
    textRef.current = option.name;
    setDraftId(option.id);
    takenIdRef.current = option.id;
    leaveByKeyboard();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // The row's own keyboard handling (selection, arrow-key focus) must not see the box's keys.
    event.stopPropagation();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!listOpen) { setListOpen(true); setActive(0); return; }
      const count = shown.length;
      if (!count) return;
      setActive(prev => (prev + (event.key === 'ArrowDown' ? 1 : -1) + count) % count);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      if (listOpen && active >= 0 && shown[active]) choose(shown[active]);
      else leaveByKeyboard();
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      if (listOpen && active >= 0 && shown[active]) takenIdRef.current = shown[active].id;
      leaveByKeyboard();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelledRef.current = true;
      leaveByKeyboard();
    }
  };

  const stop = (event: React.SyntheticEvent) => event.stopPropagation();

  const pendingHere = pendingFor(pending, ticketId);
  const restingId = pendingHere ? pendingHere.categoryId : categoryId;
  const restingName = restingTitle(restingId);

  return (
    <ProviderConsumer
      render={globalTheme => {
        const { colorScheme } = globalTheme.siteVariables;
        const marked = markClass(saveMark.mark, saveMark.edited);
        // Under a dialog (held) the cell is outlined instead; the dialog is what is waiting.
        const lock = savingCell(saveMark.busy && !held);
        if (!editing) {
          return (
            <div
              ref={restingRef}
              className={`inline-edit-cell${marked}${lock.className}`}
              {...lock.props}
              data-selection-disabled={true}
              {...hoverTitle(restingName)}
              tabIndex={0}
              role="button"
              aria-label={`${label}: ${restingName}`}
              aria-expanded={held || undefined}
              onMouseDown={stop}
              onClick={event => { stop(event); startEditing(); }}
              onKeyDown={event => {
                if (event.key === 'Enter' || event.key === 'F2') { stop(event); event.preventDefault(); startEditing(); }
              }}
              style={{
                ...CELL_BOX,
                
                cursor: 'text',
                outline: held ? `2px solid ${colorScheme.brand.foreground}` : undefined,
                outlineOffset: -2, ...saveMark.markStyle,
                ...ThemeColorScheme(globalTheme.siteVariables)
              }}
            >
              {renderResting(restingId)}
            </div>
          );
        }
        // Red for text that matches no option, or a value emptied (the box cannot save a blank) - not for a box that
        // opened blank on a field with no value (an optional list property), where there is nothing wrong yet.
        const unmatched = draftId == null && (text.trim() !== '' || (categoryId != null && !clearable));
        let borderColor = colorScheme.brand.foreground;
        if (saveMark.mark === 'failed') borderColor = SAVE_ERROR_COLOR;
        else if (unmatched) borderColor = colorScheme.red.foreground;
        return (
          <div data-selection-disabled={true} onMouseDown={stop} onClick={stop} onKeyDown={stop}>
            <input
              ref={inputRef}
              type="text"
              role="combobox"
              data-inline-editor="true"
              aria-expanded={listOpen}
              aria-autocomplete="list"
              aria-label={label}
              aria-invalid={unmatched || saveMark.mark === 'failed'}
              autoComplete="off"
              spellCheck={false}
              value={text}
              onChange={event => {
                const typed = event.target.value;
                const resolved = resolveOption(options, typed);
                const matches = matchingOptions(options, typed);
                setText(typed);
                textRef.current = typed;
                setDraftId(resolved);
                setFilter(typed);
                // The row Enter will take is lit as you type: what the text resolves to, else the top match.
                const resolvedAt = matches.findIndex(option => option.id === resolved);
                setActive(!typed.trim() || !matches.length ? -1 : Math.max(resolvedAt, 0));
                setListOpen(true);
              }}
              onClick={() => { if (!listOpen) { setFilter(''); setListOpen(true); setActive(options.findIndex(option => option.id === draftId)); } }}
              onKeyDown={onKeyDown}
              onBlur={leave}
              style={{
                ...CELL_BOX,
                // The cell's own type, so the words do not change size as the box opens - an input does not
                // inherit font by default. The border sits inside the box, so the padding gives its 1px back.
                font: 'inherit',
                paddingLeft: 11,
                width: '100%',
                margin: 0,
                outline: 'none',
                border: `1px solid ${borderColor}`,
                backgroundColor: colorScheme.default.background,
                color: colorScheme.default.foreground,
                ...(clearable ? { paddingRight: 28 } : {})
              }}
            />
            {clearable && text && (
              <button
                type="button"
                aria-label={`${i18next.t('time-tracking.clear')} ${label}`}
                title={`${i18next.t('time-tracking.clear')} ${label}`}
                tabIndex={-1}
                // Pressing it must not take focus out of the box first - that blur would leave it with the old value.
                onMouseDown={event => event.preventDefault()}
                onClick={event => { stop(event); clear(); }}
                style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', display: 'flex', border: 0, padding: 2, background: 'transparent', cursor: 'pointer', color: colorScheme.default.foreground, zIndex: 1 }}
              >
                <CloseIcon size="smaller" />
              </button>
            )}
            {listOpen && position && (
              <TypeaheadList
                listRef={listRef}
                position={position}
                shown={shown}
                active={active}
                selectedId={draftId}
                filter={filter}
                globalTheme={globalTheme}
                onPick={choose}
                onHover={setActive}
                size={CELL_LIST_SIZE}
              />
            )}
          </div>
        );
      }}
    />
  );
};
